import {areSetsEqual, objectFromEntries} from '@benchling/js-utils';
import {partition} from '@benchling/js-utils';
import {produce} from 'immer';
import {ActorRefFrom} from 'xstate';

import {assert} from 'Asserts';
import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {MinimalSchemaTag} from 'folder-items/FileConstants';
import {Pagination} from 'lib/Listings';
import {ConcentrationLayerMachine} from 'samples/plate-maps/concentration-layers/machine/ConcentrationLayerMachine';
import {mergeSourceTestOrders} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import {PatternParametersInput} from 'samples/plate-maps/pattern-controls/AssignWellsByPattern';
import {
  DEFAULT_PATTERN_PARAMS,
  PatternAssignmentParameters,
  PlateDataParameter,
  PlateMap,
  PlateMaterialInput,
  PreviewSourceError,
  ReagentAssignmentGroup,
  SchematizedDisplayLayerMetadata,
  Source,
  SourceErrorsBySourceDilutionId,
  TestOrderInfoById,
  WellAssignmentMethod,
} from 'samples/plate-maps/PlateMapConstants';
import {
  getDataParameterNamesByVersionId,
  getGroupIdsForReagents,
  getPaginationOffset,
  getPlateMapWithPatternPreview,
  getSelectedRoleGroups,
} from 'samples/plate-maps/PlateMapUtil';
import {
  FillPlateTransferTableViewMode,
  ReagentMeasurementCellsBySchemas,
  ReagentMeasurementCellsByWells,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {omit} from 'util/omitKey';

export interface FillPlateModalSharedState {
  plateMap: PlateMap | null;
  liveSchemaFieldsById: {[id: string]: SchemaField};
  plateDataParametersById: {[id: string]: PlateDataParameter};
  plateMaterialInputsById: {[id: string]: PlateMaterialInput};
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  selectedPlateDataParameterIds: Set<string>;
  /** Actor reference for the concentration layer machine */
  concentrationLayerMachine?: ActorRefFrom<typeof ConcentrationLayerMachine>;
}

export interface FillPlateModalReagentStepState {
  isInProcedure: boolean;
  patternParams: PatternParametersInput;
  previewPlateMap: PlateMap | null;
  previewedSourcesBySourceDilutionId: {[sourceDilutionId: string]: Source};
  previewedSourceDilutionIdOrdering: Array<string>;
  /** Error messages for previewed sources to render in the reagent settings pane. */
  previewedSourceErrorsBySourceDilutionId: SourceErrorsBySourceDilutionId;
  selectedWellIndices: Set<number>;
  wellAssignmentMethod: WellAssignmentMethod;
  assignByRoleGroups: boolean;
  canAssignByRoleGroups: boolean;
  selectedWellSchemaFieldIds: Set<string>;
}

export interface FillPlateModalTransferStepState {
  /** Information about how to page through the selected wells. */
  pagination: Pagination;
  /** Whether the table is looking at the plate as a whole (i.e. the reagent schemas) or each well in the plate. */
  viewMode: FillPlateTransferTableViewMode;
}

export type FillPlateModalState = FillPlateModalSharedState &
  FillPlateModalReagentStepState &
  FillPlateModalTransferStepState;

export type FillPlateModalStateWithPlateMap = FillPlateModalState & {
  plateMap: PlateMap;
  previewPlateMap: PlateMap;
};

const FIELDS_TO_RETAIN_ON_REINITIALIZE: Record<keyof FillPlateModalState, boolean> = {
  plateMap: false,
  liveSchemaFieldsById: true,
  plateDataParametersById: true,
  plateMaterialInputsById: true,
  reagentGroupsById: false,
  selectedPlateDataParameterIds: true,
  concentrationLayerMachine: true,
  isInProcedure: true,
  patternParams: false,
  previewPlateMap: false,
  previewedSourcesBySourceDilutionId: false,
  previewedSourceDilutionIdOrdering: false,
  selectedWellIndices: false,
  wellAssignmentMethod: false,
  assignByRoleGroups: false,
  canAssignByRoleGroups: false,
  pagination: false,
  viewMode: false,
  selectedWellSchemaFieldIds: true,
  previewedSourceErrorsBySourceDilutionId: false,
};
export const FIELD_NAMES_TO_RETAIN_ON_REINITIALIZE = Object.entries(FIELDS_TO_RETAIN_ON_REINITIALIZE)
  .filter(([_, retain]) => retain)
  .map(([field]) => field as keyof FillPlateModalState);

export const DEFAULT_FILL_PLATE_MODAL_STATE: FillPlateModalState = {
  isInProcedure: false,
  pagination: {offset: 0, limit: 0, total: 0, isExact: true},
  patternParams: DEFAULT_PATTERN_PARAMS,
  plateMap: null,
  liveSchemaFieldsById: {},
  plateDataParametersById: {},
  plateMaterialInputsById: {},
  previewPlateMap: null,
  previewedSourcesBySourceDilutionId: {},
  previewedSourceDilutionIdOrdering: [],
  reagentGroupsById: {},
  selectedPlateDataParameterIds: new Set(),
  selectedWellIndices: new Set(),
  wellAssignmentMethod: WellAssignmentMethod.PATTERN,
  viewMode: FillPlateTransferTableViewMode.SUMMARY,
  assignByRoleGroups: true,
  canAssignByRoleGroups: true,
  selectedWellSchemaFieldIds: new Set(),
  previewedSourceErrorsBySourceDilutionId: {[PreviewSourceError.INVALID_TRIAL_SAMPLE]: {}},
};

export enum FillPlateModalReducerActionType {
  INITIALIZE_PLATE_MAP = 'INITIALIZE_PLATE_MAP',
  INITIALIZE_PROCEDURE_DATA = 'INITIALIZE_PROCEDURE_DATA',
  ADD_SELECTED_PLATE_DATA_PARAMETER = 'ADD_SELECTED_PLATE_DATA_PARAMETER',
  ADD_SELECTED_WELL_SCHEMA_FIELD = 'ADD_SELECTED_WELL_SCHEMA_FIELD',
  ADD_SOURCES_AND_SCHEMAS = 'ADD_SOURCES_AND_SCHEMAS',
  APPLY_SOURCES = 'APPLY_SOURCES',
  REMOVE_ALL_APPLIED_SOURCE_GROUPS = 'REMOVE_ALL_APPLIED_SOURCE_GROUPS',
  REMOVE_APPLIED_SOURCE_GROUPS = 'REMOVE_APPLIED_SOURCE_GROUPS',
  REMOVE_SOURCES = 'REMOVE_SOURCES',
  REMOVE_PREVIEWED_SOURCES = 'REMOVE_PREVIEWED_SOURCES',
  REMOVE_SELECTED_PLATE_DATA_PARAMETER = 'REMOVE_SELECTED_PLATE_DATA_PARAMETER',
  REMOVE_SELECTED_WELL_SCHEMA_FIELD = 'REMOVE_SELECTED_WELL_SCHEMA_FIELD',
  REMOVE_CONTENT_GROUPS = 'REMOVE_CONTENT_GROUPS',
  REORDER_SOURCES = 'REORDER_SOURCES',
  SET_ASSIGN_BY_ROLE_GROUPS = 'SET_ASSIGN_BY_ROLE_GROUPS',
  SET_INITIAL_SELECTED_WELL_SCHEMA_FIELDS = 'SET_INITIAL_SELECTED_WELL_SCHEMA_FIELDS',
  SET_PATTERN_PARAMS = 'SET_PATTERN_PARAMS',
  SET_SELECTED_WELLS = 'SET_SELECTED_WELLS',
  SET_TOTAL_WELL_QUANTITY_VALUE = 'SET_TOTAL_WELL_QUANTITY_VALUE',
  SET_TOTAL_WELL_QUANTITY_UNITS = 'SET_TOTAL_WELL_QUANTITY_UNITS',
  SET_VIEW_MODE = 'SET_VIEW_MODE',
  SET_WELL_ASSIGNMENT_METHOD = 'SET_WELL_ASSIGNMENT_METHOD',
  UPDATE_PAGINATION_OFFSET = 'UPDATE_PAGINATION_OFFSET',
  UPDATE_PLATE_MAP_ANNOTATIONS = 'UPDATE_PLATE_MAP_ANNOTATIONS',
  UPDATE_REAGENT_MEASUREMENTS_BY_SCHEMAS = 'UPDATE_REAGENT_MEASUREMENTS_BY_SCHEMAS',
  UPDATE_REAGENT_MEASUREMENTS_BY_WELLS = 'UPDATE_REAGENT_MEASUREMENTS_BY_WELLS',
}

interface FillPlateModalAction {
  type: FillPlateModalReducerActionType;
}

interface InitializePlateMapAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.INITIALIZE_PLATE_MAP;
  plateMap: PlateMap;
  assignmentGroups: {[id: string]: ReagentAssignmentGroup};
}

interface InitializePlateDataParametersAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.INITIALIZE_PROCEDURE_DATA;
  plateDataParameters: Array<PlateDataParameter>;
  calculateInitialDataParameters: boolean;
  plateMaterialInputsById: {[id: string]: PlateMaterialInput};
}

export interface AddSelectedPlateDataParameterAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.ADD_SELECTED_PLATE_DATA_PARAMETER;
  parameterVersionId: string;
}

export interface AddSelectedWellSchemaFieldAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.ADD_SELECTED_WELL_SCHEMA_FIELD;
  schemaFieldId: string;
}

interface AddSourcesAndSchemasAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.ADD_SOURCES_AND_SCHEMAS;
  reagents: Array<Source>;
  schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}};
  testOrderInfoById: TestOrderInfoById | null;
  testOrderErrorsBySourceDilutionId?: {[sourceDilutionId: string]: string};
}

interface ApplySourcesAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.APPLY_SOURCES;
}

interface RemoveAllAppliedSourceGroupsAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.REMOVE_ALL_APPLIED_SOURCE_GROUPS;
  /** If true, removes the source groups from all wells; otherwise removes only from selected wells */
  clearAllWells: boolean;
}

interface RemoveAppliedSourceGroupsAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.REMOVE_APPLIED_SOURCE_GROUPS;
  reagentGroupIds: Set<string>;
  /** If true, removes the source groups from all wells; otherwise removes only from selected wells */
  clearAllWells: boolean;
}

interface RemoveContentGroupsAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.REMOVE_CONTENT_GROUPS;
  contentGroupIds: Set<string>;
  /** If true, removes the content groups from all wells; otherwise removes only from selected wells */
  clearAllWells: boolean;
}

interface RemoveInvalidSourcesAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.REMOVE_SOURCES;
  sourceIds: Set<string>;
}

interface RemovePreviewedSourcesAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.REMOVE_PREVIEWED_SOURCES;
  sourceDilutionIds: Array<string>;
}

interface RemoveSelectedPlateDataParameterAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.REMOVE_SELECTED_PLATE_DATA_PARAMETER;
  parameterVersionId: string;
}

interface RemoveSelectedWellSchemaFieldAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.REMOVE_SELECTED_WELL_SCHEMA_FIELD;
  schemaFieldId: string;
  /** What schema tag should be set for this schema field, by well position.
   * If null, tags will be removed instead.
   */
  tagsToRevertTo: Array<MinimalSchemaTag> | null;
}

interface ReorderSourcesAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.REORDER_SOURCES;
  sourceDilutionIdOrdering: Array<string>;
}

interface SetAssignByRoleGroupsAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.SET_ASSIGN_BY_ROLE_GROUPS;
  assignByRoleGroups: boolean;
}

interface SetInitialSelectedWellSchemaFields extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.SET_INITIAL_SELECTED_WELL_SCHEMA_FIELDS;
  schemaFieldLayers: Array<SchematizedDisplayLayerMetadata>;
  initialSelectedLayerIds: Array<string> | null;
}

interface SetPatternParamsAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.SET_PATTERN_PARAMS;
  patternParams: PatternParametersInput;
}

interface SetSelectedWellsAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.SET_SELECTED_WELLS;
  selectedWells: Set<number>;
}

interface SetWellAssignmentMethodAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.SET_WELL_ASSIGNMENT_METHOD;
  wellAssignmentMethod: WellAssignmentMethod;
}

interface SetTotalWellQuantityValueAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.SET_TOTAL_WELL_QUANTITY_VALUE;
  totalWellQuantityValue: number | null;
}

interface SetTotalWellQuantityUnitAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.SET_TOTAL_WELL_QUANTITY_UNITS;
  totalWellQuantityUnit: MeasurementUnit | null;
}

interface SetViewModeAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.SET_VIEW_MODE;
  viewMode: FillPlateTransferTableViewMode;
}

interface UpdatePaginationOffsetAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.UPDATE_PAGINATION_OFFSET;
  offset: number;
}

interface UpdateReagentMeasurementCellsBySchemasAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.UPDATE_REAGENT_MEASUREMENTS_BY_SCHEMAS;
  reagentMeasurementCellsBySchemas: ReagentMeasurementCellsBySchemas;
}

interface UpdatePlateMapAnnotationsAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.UPDATE_PLATE_MAP_ANNOTATIONS;
  plateMap: PlateMap;
}

interface UpdateReagentMeasurementCellsByWellsAction extends FillPlateModalAction {
  type: FillPlateModalReducerActionType.UPDATE_REAGENT_MEASUREMENTS_BY_WELLS;
  reagentMeasurementCellsByWells: ReagentMeasurementCellsByWells;
}

export type FillPlateModalReducerAction =
  | InitializePlateMapAction
  | InitializePlateDataParametersAction
  | AddSelectedPlateDataParameterAction
  | AddSelectedWellSchemaFieldAction
  | AddSourcesAndSchemasAction
  | ApplySourcesAction
  | RemoveAllAppliedSourceGroupsAction
  | RemoveAppliedSourceGroupsAction
  | RemoveInvalidSourcesAction
  | RemovePreviewedSourcesAction
  | RemoveSelectedPlateDataParameterAction
  | RemoveSelectedWellSchemaFieldAction
  | RemoveContentGroupsAction
  | ReorderSourcesAction
  | SetAssignByRoleGroupsAction
  | SetInitialSelectedWellSchemaFields
  | SetPatternParamsAction
  | SetSelectedWellsAction
  | SetTotalWellQuantityValueAction
  | SetTotalWellQuantityUnitAction
  | SetViewModeAction
  | SetWellAssignmentMethodAction
  | UpdatePaginationOffsetAction
  | UpdatePlateMapAnnotationsAction
  | UpdateReagentMeasurementCellsBySchemasAction
  | UpdateReagentMeasurementCellsByWellsAction;

/**
 * Exported for testing.
 * Intended to be used AFTER updating the previewed sources state, source ordering, and reagent assignment group map.
 * Applies labels to sources if applying by pattern or using role groups; strips labels if applying by group.
 */
export function hydrateSourceReagentLabels(state: FillPlateModalStateWithPlateMap): {[id: string]: Source} {
  if (state.wellAssignmentMethod === WellAssignmentMethod.SPECIFY_GROUP) {
    return objectFromEntries(
      Object.entries(state.previewedSourcesBySourceDilutionId).map(([sourceDilutionId, source]) => [
        sourceDilutionId,
        // TypeScript gets confused about whether reagent can still be a valid Reagent after the
        // omit even though it's only omitting something that's shared between all types of
        // Reagent.
        omit(source, 'label') as Source,
      ])
    );
  } else if (
    state.assignByRoleGroups &&
    state.previewedSourceDilutionIdOrdering.length > 0 &&
    state.selectedWellIndices.size > 0
  ) {
    const orderedGroups = getSelectedRoleGroups(state.plateMap, state.selectedWellIndices);
    return objectFromEntries(
      state.previewedSourceDilutionIdOrdering.map((sourceDilutionId, index) => [
        sourceDilutionId,
        {...state.previewedSourcesBySourceDilutionId[sourceDilutionId], label: orderedGroups[index] ?? null},
      ])
    );
  } else {
    return objectFromEntries(
      // Assign labels according to the reagent ordering.
      state.previewedSourceDilutionIdOrdering.map((sourceDilutionId, index) => [
        sourceDilutionId,
        // Pattern groups are 1-indexed; adjust from array index
        {...state.previewedSourcesBySourceDilutionId[sourceDilutionId], label: index + 1},
      ])
    );
  }
}

/**
 * Exported for testing.
 * This function is intended to be used anytime before updating the plate map preview.
 * Given the existing plate map and all known data parameters, populate all the data parameter names.
 */
export function maybeHydrateDataParameterNames(state: FillPlateModalStateWithPlateMap): PlateMap {
  const allDataParameters = Object.values(state.plateDataParametersById);
  const allKnownDataParameterVersionIds = new Set(
    allDataParameters.map((plateDataParameter) => plateDataParameter.versionId)
  );
  const plateMapParameterVersionIds = new Set(
    Object.keys(state.plateMap.dataParameterNamesByVersionId ?? {})
  );
  if (areSetsEqual(allKnownDataParameterVersionIds, plateMapParameterVersionIds)) {
    return state.plateMap;
  }
  return {
    ...state.plateMap,
    dataParameterNamesByVersionId: getDataParameterNamesByVersionId(allDataParameters),
  };
}

/**
 * Exported for testing.
 * This function is intended to be used AFTER deleting reagents in the plate map and/or previewed reagent map
 * and BEFORE updating the plate map preview. Given the new plate map or reagent preview map, calculate what
 * the new reagent group map should be.
 */
export function filterUnusedReagentGroups(state: FillPlateModalStateWithPlateMap): {
  [id: string]: ReagentAssignmentGroup;
} {
  // Find and dedupe all group ids in the entire plate map and the preview
  const allReagentGroups = getGroupIdsForReagents(
    [
      ...state.plateMap.wells.flatMap((well) => well.sources),
      ...Object.values(state.previewedSourcesBySourceDilutionId),
    ],
    state.plateMap.wells.flatMap((well) => well.contents)
  );

  // Retain all reagent groups that are still in the plate map + preview; omit all reagent groups that are not
  const newReagentGroupsById: {[id: string]: ReagentAssignmentGroup} = {};
  for (const reagentGroupId of Object.keys(state.reagentGroupsById)) {
    if (allReagentGroups.has(reagentGroupId)) {
      newReagentGroupsById[reagentGroupId] = state.reagentGroupsById[reagentGroupId];
    }
  }
  return newReagentGroupsById;
}

/**
 * Update well sources to add new ones. Duplicate incoming sources generally clobber existing ones, except
 * for test orders which get merged. Used to update preview plate map.
 *
 * This function expects a mapping of sources keyed by actual id, not source dilution id - any duplicates
 * within the incoming sources should have already been resolved.
 *
 * @param existingSources Pre-existing sources from the well
 * @param incomingSourcesById New sources, mapped by source.id.
 */
function updateWellSources(
  existingSources: Array<Source>,
  incomingSourcesById: {[sourceId: string]: Source}
): Array<Source> {
  const incomingSourceIds = new Set(Object.keys(incomingSourcesById));

  const [distinctSources, dupSources] = partition(
    existingSources,
    (source) => !incomingSourceIds.has(source.id)
  );
  const dupSourcesById = objectFromEntries(dupSources.map((source) => [source.id, source]));
  // Merge incoming source with any duplicates. Appends the incoming source unchanged in the case of
  // no matching duplicate.
  const newSources = Object.values(incomingSourcesById).map((incomingSource) =>
    mergeSourceTestOrders(incomingSource, dupSourcesById[incomingSource.id])
  );
  return [...distinctSources, ...newSources];
}

/**
 * Exported for testing.
 * Intended to be used AFTER updating the reagent map, reagent ordering, reagent group map, and well
 * selection. Applies reagents to the selected cells according to the specified well assignment method.
 */
export function makePreviewPlateMap(state: FillPlateModalStateWithPlateMap): PlateMap {
  return produce(state.plateMap, (draft) => {
    if (state.wellAssignmentMethod === WellAssignmentMethod.SPECIFY_GROUP) {
      // Merge incoming source test orders to remove dilution duplicates
      const incomingSourcesBySourceId = Object.values(state.previewedSourcesBySourceDilutionId).reduce(
        (sourcesById: {[sourceId: string]: Source}, source) => {
          sourcesById[source.id] = mergeSourceTestOrders(source, sourcesById[source.id]);
          return sourcesById;
        },
        {}
      );
      for (const index of state.selectedWellIndices) {
        // If any sources to add are already in the well, merge test orders but otherwise clobber them.
        draft.wells[index].sources = updateWellSources(draft.wells[index].sources, incomingSourcesBySourceId);
      }
    } else if (
      state.assignByRoleGroups &&
      state.previewedSourceDilutionIdOrdering.length > 0 &&
      state.selectedWellIndices.size > 0
    ) {
      const orderedGroups = getSelectedRoleGroups(state.plateMap, state.selectedWellIndices);
      const groupsToOrderIndex = objectFromEntries(orderedGroups.map((group, idx) => [group, idx]));
      for (const wellIndex of state.selectedWellIndices) {
        const group = state.plateMap.wells[wellIndex].role!.group;
        const orderIdx = groupsToOrderIndex[group];
        const sourceToAdd =
          state.previewedSourcesBySourceDilutionId[state.previewedSourceDilutionIdOrdering[orderIdx]];
        if (sourceToAdd != null) {
          // If the reagent is already in the well, merge test orders but otherwise clobber it.
          draft.wells[wellIndex].sources = updateWellSources(draft.wells[wellIndex].sources, {
            [sourceToAdd.id]: sourceToAdd,
          });
        }
      }
    } else if (
      state.patternParams.replicates != null &&
      state.patternParams.replicates > 0 &&
      state.selectedWellIndices.size > 0
    ) {
      const plateMapWithPatternPreview = getPlateMapWithPatternPreview(
        draft,
        (patternGroupNumber, wellIndex) => {
          if (patternGroupNumber > state.previewedSourceDilutionIdOrdering.length) {
            // If we have more pattern groups than we have reagents, don't change anything about the well.
            return {};
          } else {
            const patternReagent =
              state.previewedSourcesBySourceDilutionId[
                state.previewedSourceDilutionIdOrdering[patternGroupNumber - 1]
              ];
            // If the reagent at this pattern group number is already in the well, merge test orders but
            // otherwise clobber it.
            return {
              sources: updateWellSources(draft.wells[wellIndex].sources, {
                [patternReagent.id]: patternReagent,
              }),
            };
          }
        },
        state.patternParams as PatternAssignmentParameters,
        state.selectedWellIndices
      );
      draft.wells = plateMapWithPatternPreview.wells;
    }
  });
}

export function updatePagination(state: FillPlateModalStateWithPlateMap): Pagination {
  // Page through the selected wells that have contents or sources.
  const limit = state.plateMap.size.numColumns;
  // If nothing is selected, default to showing everything.
  const total =
    state.selectedWellIndices.size > 0 ? state.selectedWellIndices.size : state.plateMap.wells.length;
  const offset = getPaginationOffset(state.pagination.offset, limit, total);
  return {
    ...state.pagination,
    offset,
    limit,
    total,
  };
}

/**
 * This should be called at the end of most switch cases. It must be called after doing any of the following:
 * - adding, removing, or reordering reagents
 * - updating pattern params
 * - changing well assignment method
 * - editing reagent properties such as concentration
 */
export function updateDependentState(state: FillPlateModalState): FillPlateModalState {
  assert(state.plateMap != null && state.previewPlateMap != null, 'Expected plate map to be initialized');
  return produce(state as FillPlateModalStateWithPlateMap, (draft) => {
    const canAssignByRoleGroups = getCanAssignByRoleGroups(draft.selectedWellIndices, draft.plateMap);
    draft.assignByRoleGroups = canAssignByRoleGroups ? draft.assignByRoleGroups : false;
    draft.canAssignByRoleGroups = canAssignByRoleGroups;
    draft.previewedSourcesBySourceDilutionId = hydrateSourceReagentLabels(draft);
    draft.reagentGroupsById = filterUnusedReagentGroups(draft);
    draft.plateMap = maybeHydrateDataParameterNames(draft);
    draft.previewPlateMap = makePreviewPlateMap(draft);
    draft.pagination = updatePagination(draft);
  });
}

/**
 * Whether all the selected wells have the same primary and sub role assigned and that role is not null.
 */
function getCanAssignByRoleGroups(selectedWells: Set<number>, plateMap: PlateMap): boolean {
  if (selectedWells.size === 0) {
    return true;
  } else {
    const selectedWellsArray = [...selectedWells];
    const selectedPrimaryRoles = new Set(
      selectedWellsArray.map((idx) => plateMap.wells[idx].role?.primaryRole)
    );
    const selectedSubroles = new Set(selectedWellsArray.map((idx) => plateMap.wells[idx].role?.subrole));
    return (
      selectedPrimaryRoles.size === 1 &&
      selectedSubroles.size === 1 &&
      selectedPrimaryRoles.values().next().value != null
    );
  }
}
